import * as PIXI from 'pixi.js';
import md5 from 'md5';

import { FONT_STATUS } from '../../constants';
import type { FontManager } from '../FontManager';

type FontLoaderSettings = {
    parent: FontManager;
    sourceUrl: string;
};

/**
 * The FontLoader class is created by the FontManager and loads a given font.
 */
export class FontLoader extends PIXI.utils.EventEmitter {
    parent = undefined;
    classType = 'Font';
    #status: FONT_STATUS = FONT_STATUS.ADDED;
    #sourceUrl: string;
    #familyName: string;
    #fontFace: FontFace;

    /**
     * The class name is simply the hash of the url – this guarantees a one way mapping
     * without requiring any additional information about the font with the stripped name
     * to help with debugging (also classes cannot start with numbers)
     */
    static #generateCssFamilyName(fontUrl: string): string {
        if (fontUrl === null || fontUrl === undefined) {
            return '';
        }
        const filename = fontUrl
            .substring(fontUrl.lastIndexOf('/') + 1)
            .replace('.ttf', '')
            .replace('.otf', '')
            .replace(/[^a-z+]+/gi, '');
        return `${filename}-${md5(fontUrl).substr(0, 5)}`;
    }

    constructor(settings: FontLoaderSettings) {
        super();
        this.parent = settings.parent;
        this.#sourceUrl = settings.sourceUrl;
    }

    /**
     * Loads the specified sourceUrl.
     */
    async load(): Promise<void> {
        try {
            const fontFace = new FontFace(
                this.familyName,
                `url(${encodeURI(decodeURI(this.#sourceUrl))})`,
            );
            document.fonts.add(fontFace);
            await fontFace.load();
            this.#status = FONT_STATUS.LOADED;
        } catch (error) {
            this.#status = FONT_STATUS.FAILED;
            throw error;
        }
    }

    get isLoaded() {
        return this.#status === FONT_STATUS.LOADED;
    }

    get failedToLoad() {
        return this.#status === FONT_STATUS.FAILED;
    }

    get isDoneTryingToLoad() {
        return this.isLoaded || this.failedToLoad;
    }

    get fullName() {
        return this.#sourceUrl
            .substring(this.#sourceUrl.lastIndexOf('/') + 1)
            .replace('.ttf', '')
            .replace('.otf', '')
            .replace(/[^a-z+]+/gi, '');
    }

    /**
     * Return font familyName if it is defined in the font, otherwise returns
     * a md5 hash based on the source URL.
     */
    get familyName(): string {
        if (this.#familyName) {
            return this.#familyName;
        }
        this.#familyName = FontLoader.#generateCssFamilyName(this.#sourceUrl);
        return this.#familyName;
    }

    destroy() {
        this.#status = FONT_STATUS.DESTROYED;
        this.removeAllListeners();
        if (this.#fontFace) {
            document.fonts.delete(this.#fontFace);
        }
    }

    get sourceUrl() {
        return this.#sourceUrl;
    }

    get status() {
        return this.#status;
    }
}
